import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { FIRM, PRACTICE_AREAS, ATTORNEYS, STAFF } from "../mock";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  const navItems = [
    { label: "About", href: "/about" },
    {
      label: "Our Team",
      href: "/attorneys",
      children: [
        ...ATTORNEYS.map((a) => ({
          label: `${a.firstName} ${a.lastName}`,
          href: `/attorneys/${a.slug}`,
        })),
        ...STAFF.map((s) => ({
          label: `${s.firstName} ${s.lastName}`,
          href: `/team/${s.slug}`,
        })),
      ],
    },
    {
      label: "Practice Areas",
      href: "/practice-areas",
      children: PRACTICE_AREAS.slice(0, 8).map((p) => ({
        label: p.title,
        href: `/practice-areas#${p.slug}`,
      })),
    },
    { label: "Contact", href: "/contact" },
  ];

  return (
    <>
      {/* Top thin bar */}
      <div className="hidden md:block bg-nw-navy-dark text-white/90 text-xs">
        <div className="container-nw flex items-center justify-between h-9">
          <div className="flex items-center gap-6">
            <span className="tracking-wide">Charleston, South Carolina</span>
            <span className="text-white/40">|</span>
            <span className="tracking-wide">{FIRM.phone}</span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/contact" className="link-underline hover:text-nw-gold-light transition-colors">
              Schedule a Consultation
            </Link>
          </div>
        </div>
      </div>

      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white shadow-md border-b border-nw-stone"
            : "bg-white/95 backdrop-blur border-b border-nw-stone/60"
        }`}
      >
        <div className="container-nw">
          <div className="flex items-center justify-between h-24 lg:h-32">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              <img
                src={FIRM.logo}
                alt="Newberry Wieters Law Firm"
                className="h-20 lg:h-24 w-auto object-contain"
              />
            </Link>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-8">
              {navItems.map((item) => (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={() => item.children && setOpenDropdown(item.label)}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <Link
                    to={item.href}
                    className={`flex items-center gap-1 py-2 text-sm font-medium tracking-wide uppercase transition-colors ${
                      location.pathname === item.href
                        ? "text-nw-gold"
                        : "text-nw-navy hover:text-nw-gold"
                    }`}
                  >
                    {item.label}
                    {item.children && <ChevronDown className="w-3.5 h-3.5" />}
                  </Link>
                  {item.children && openDropdown === item.label && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 min-w-[260px] z-50">
                      <div className="bg-white border border-nw-stone shadow-xl rounded-sm py-3">
                        {item.children.map((c) => (
                          <Link
                            key={c.href}
                            to={c.href}
                            className="block px-5 py-2.5 text-sm text-nw-navy hover:bg-nw-cream hover:text-nw-gold transition-colors"
                          >
                            {c.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* CTA */}
            <div className="hidden lg:flex items-center gap-3">
              <Link
                to="/contact"
                className="px-5 py-2.5 bg-nw-navy text-white text-xs font-semibold uppercase tracking-wider hover:bg-nw-navy-light transition-colors"
              >
                Contact Us
              </Link>
            </div>

            {/* Mobile toggle */}
            <button
              className="lg:hidden text-nw-navy p-2"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden bg-white border-t border-nw-stone">
            <div className="container-nw py-4 flex flex-col gap-1">
              {navItems.map((item) => (
                <div key={item.label} className="border-b border-nw-stone/60 last:border-b-0">
                  <Link
                    to={item.href}
                    className="block py-3 text-nw-navy font-medium tracking-wide uppercase text-sm"
                  >
                    {item.label}
                  </Link>
                </div>
              ))}
              <Link
                to="/contact"
                className="mt-4 px-5 py-3 bg-nw-navy text-white text-xs font-semibold uppercase tracking-wider text-center"
              >
                Contact Us
              </Link>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Navbar;
